import { useMemo } from "react";

import type { SearchableSelectOption } from "@/core/components/ui/searchable-select";
import { endpoints } from "@/core/api/endpoints";
import { QUERY_KEYS } from "@/core/api/query-keys";
import {
  ENTITY_SELECT_LIST_PARAMS,
  usePagedSelectOptions,
} from "@/core/api/use-paged-select-options";

const joinLabel = (
  ...parts: Array<string | null | undefined>
): string =>
  parts
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(" · ");

const toOption = (
  id: string | undefined,
  label: string,
  /** Description métier. Absente : l’UUID sert de description si le libellé diffère. */
  description?: string
): SearchableSelectOption | null => {
  if (!id) return null;
  const trimmed = label.trim() || id;
  const extra =
    description !== undefined
      ? description.trim()
      : trimmed === id
        ? ""
        : id;
  return {
    value: id,
    label: trimmed,
    description: extra && extra !== trimmed ? extra : undefined,
  };
};

/** Formes minimales (pas de DTO kyc/wallet) — le JSON API est un sur-ensemble. */
type PartySelectSource = {
  id?: string;
  keycloakUserId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  email?: string | null;
  partyType?: string | null;
};

type WalletSelectSource = {
  id?: string;
  walletNumber?: string | null;
  ownerName?: string | null;
  ownerPartyId?: string | null;
  keycloakUserId?: string | null;
  currency?: string | null;
  status?: string | null;
  ownerType?: string | null;
};

type TransactionSelectSource = {
  id?: string;
  reference?: string | null;
  type?: string | null;
  status?: string | null;
  productCode?: string | null;
};

type AgentSelectSource = {
  id?: string;
  keycloakUserId?: string | null;
  fullName?: string | null;
  agencyCode?: string | null;
  agentType?: string | null;
};

type ProviderSelectSource = {
  id?: string;
  code?: string | null;
  name?: string | null;
  type?: string | null;
};

type AccountSelectSource = {
  id?: string;
  code?: string | null;
  label?: string | null;
  accountType?: string | null;
};

type ProductCodeSelectSource = {
  id?: string;
  code?: string | null;
  label?: string | null;
};

type SubscriptionPlanSelectSource = {
  id?: string;
  code?: string | null;
  name?: string | null;
};

type FeeRuleSelectSource = {
  id?: string;
  productCode?: string | null;
  customerSegment?: string | null;
  feePurpose?: string | null;
  calculationType?: string | null;
  status?: string | null;
  currency?: string | null;
};

export const mapPartyToSelectOption = (
  item: PartySelectSource
): SearchableSelectOption | null => {
  const name = joinLabel(item.firstName, item.lastName);
  return toOption(
    item.id,
    joinLabel(name, item.phone, item.email, item.partyType)
  );
};

export const mapWalletToSelectOption = (
  item: WalletSelectSource
): SearchableSelectOption | null =>
  toOption(
    item.id,
    item.ownerName?.trim() || item.walletNumber?.trim() || "",
    joinLabel(item.walletNumber, item.currency, item.status, item.ownerType)
  );

export const mapTransactionToSelectOption = (
  item: TransactionSelectSource
): SearchableSelectOption | null =>
  toOption(
    item.id,
    joinLabel(item.reference, item.type, item.status, item.productCode)
  );

export const mapAgentToSelectOption = (
  item: AgentSelectSource
): SearchableSelectOption | null =>
  toOption(
    item.id,
    joinLabel(item.fullName, item.agencyCode, item.agentType)
  );

export const mapProviderToSelectOption = (
  item: ProviderSelectSource
): SearchableSelectOption | null =>
  toOption(item.id, joinLabel(item.code, item.name, item.type));

export const mapAccountToSelectOption = (
  item: AccountSelectSource
): SearchableSelectOption | null =>
  toOption(item.id, joinLabel(item.code, item.label, item.accountType));

export const mapProductCodeToSelectOption = (
  item: ProductCodeSelectSource
): SearchableSelectOption | null =>
  toOption(item.id, joinLabel(item.code, item.label));

export const mapSubscriptionPlanToSelectOption = (
  item: SubscriptionPlanSelectSource
): SearchableSelectOption | null =>
  toOption(item.id, joinLabel(item.code, item.name));

export const mapFeeRuleToSelectOption = (
  item: FeeRuleSelectSource
): SearchableSelectOption | null =>
  toOption(
    item.id,
    joinLabel(
      item.productCode,
      item.customerSegment,
      item.feePurpose,
      item.calculationType,
      item.status,
      item.currency
    )
  );

/** Valeur = `keycloakUserId` (claim `sub`), pas l’UUID party. */
export const mapPartyKeycloakToSelectOption = (
  item: PartySelectSource
): SearchableSelectOption | null => {
  const name = joinLabel(item.firstName, item.lastName);
  return toOption(
    item.keycloakUserId ?? undefined,
    joinLabel(name, item.phone, item.email, item.partyType)
  );
};

/** Valeur = `keycloakUserId` de l’agent KYC. */
export const mapAgentKeycloakToSelectOption = (
  item: AgentSelectSource
): SearchableSelectOption | null =>
  toOption(
    item.keycloakUserId ?? undefined,
    joinLabel(item.fullName, item.agencyCode, item.agentType)
  );

/** Valeur = `reference` métier (ex. FPT-…), pas l’UUID transaction. */
export const mapTransactionReferenceToSelectOption = (
  item: TransactionSelectSource
): SearchableSelectOption | null =>
  toOption(
    item.reference ?? undefined,
    joinLabel(item.reference, item.type, item.status, item.productCode)
  );

/** Valeur = `code` métier (ex. WALLET_TO_WALLET), pas l’UUID ProductCode. */
export const mapProductCodeCodeToSelectOption = (
  item: ProductCodeSelectSource
): SearchableSelectOption | null =>
  toOption(item.code ?? undefined, joinLabel(item.code, item.label));

/**
 * Options d’identités (`POST /Parties/all`). Vit dans shared pour que
 * wallet/merchant/payroll/etc. n’importent pas la feature kyc.
 */
export const usePartySelectOptions = (enabled = true) =>
  usePagedSelectOptions<PartySelectSource>({
    queryKey: QUERY_KEYS.KYC_PARTIES_LIST(ENTITY_SELECT_LIST_PARAMS),
    url: endpoints.kyc.parties.all,
    mapItem: mapPartyToSelectOption,
    enabled,
  });

/**
 * Options de portefeuilles (`POST /Wallets/all`). Le `WalletFilter` serveur
 * ne couvre que party/type/status (cf. Postman) — pour restreindre à un
 * propriétaire précis, on filtre côté client via `ownerKeycloakUserId` (ou
 * `ownerPartyId`), appliqué à la liste pleine renvoyée par `/Wallets/all`.
 * `ownerType` permet de restreindre à une catégorie (INDIVIDUAL, PLATFORM,
 * MERCHANT…), utilisé par ex. sur la page emoney (opérations PLATFORM only).
 */
export const useWalletSelectOptions = (
  enabled = true,
  scope?: {
    ownerKeycloakUserId?: string | null;
    ownerPartyId?: string | null;
    ownerType?: string | null;
  }
) => {
  const kcId = scope?.ownerKeycloakUserId?.trim() || null;
  const partyId = scope?.ownerPartyId?.trim() || null;
  const ownerType = scope?.ownerType?.trim() || null;
  const filterItem = useMemo(() => {
    if (!kcId && !partyId && !ownerType) return undefined;
    return (item: WalletSelectSource) => {
      if (kcId && item.keycloakUserId !== kcId) return false;
      if (partyId && item.ownerPartyId !== partyId) return false;
      if (ownerType && item.ownerType !== ownerType) return false;
      return true;
    };
  }, [kcId, partyId, ownerType]);
  return usePagedSelectOptions<WalletSelectSource>({
    queryKey: QUERY_KEYS.WALLET_LIST(ENTITY_SELECT_LIST_PARAMS),
    url: endpoints.wallet.wallets.all,
    mapItem: mapWalletToSelectOption,
    enabled,
    filterItem,
  });
};

/** Options de transactions wallet (`POST /Transactions/all`). */
export const useTransactionSelectOptions = (enabled = true) =>
  usePagedSelectOptions<TransactionSelectSource>({
    queryKey: QUERY_KEYS.WALLET_TRANSACTIONS_LIST(ENTITY_SELECT_LIST_PARAMS),
    url: endpoints.wallet.transactions.all,
    mapItem: mapTransactionToSelectOption,
    enabled,
  });

/** Options d’agents KYC (`POST /Agents/all`). */
export const useAgentSelectOptions = (enabled = true) =>
  usePagedSelectOptions<AgentSelectSource>({
    queryKey: QUERY_KEYS.KYC_AGENTS_LIST(ENTITY_SELECT_LIST_PARAMS),
    url: endpoints.kyc.agents.all,
    mapItem: mapAgentToSelectOption,
    enabled,
  });

/** Options de providers ledger (`POST /Providers/all`). */
export const useProviderSelectOptions = (enabled = true) =>
  usePagedSelectOptions<ProviderSelectSource>({
    queryKey: QUERY_KEYS.LEDGER_PROVIDERS_LIST(ENTITY_SELECT_LIST_PARAMS),
    url: endpoints.ledger.providers.all,
    mapItem: mapProviderToSelectOption,
    enabled,
  });

/** Options du plan de comptes (`POST /ChartOfAccounts/all`). */
export const useAccountSelectOptions = (enabled = true) =>
  usePagedSelectOptions<AccountSelectSource>({
    queryKey: QUERY_KEYS.LEDGER_CHART_OF_ACCOUNTS_LIST(ENTITY_SELECT_LIST_PARAMS),
    url: endpoints.ledger.chartOfAccounts.all,
    mapItem: mapAccountToSelectOption,
    enabled,
  });

/** Options de codes produits (`POST /ProductCodes/all`). */
export const useProductCodeSelectOptions = (enabled = true) =>
  usePagedSelectOptions<ProductCodeSelectSource>({
    queryKey: QUERY_KEYS.LEDGER_PRODUCT_CODES_LIST(ENTITY_SELECT_LIST_PARAMS),
    url: endpoints.ledger.productCodes.all,
    mapItem: mapProductCodeToSelectOption,
    enabled,
  });

/** Options de plans d’abonnement (`POST /SubscriptionPlans/all`). */
export const useSubscriptionPlanSelectOptions = (enabled = true) =>
  usePagedSelectOptions<SubscriptionPlanSelectSource>({
    queryKey: QUERY_KEYS.FEE_SUBSCRIPTION_PLANS_LIST(ENTITY_SELECT_LIST_PARAMS),
    url: endpoints.feeEngine.subscriptionPlans.all,
    mapItem: mapSubscriptionPlanToSelectOption,
    enabled,
  });

/** Options de règles de frais (`POST /FeeRules/all`). */
export const useFeeRuleSelectOptions = (enabled = true) =>
  usePagedSelectOptions<FeeRuleSelectSource>({
    queryKey: QUERY_KEYS.FEE_RULES_LIST(ENTITY_SELECT_LIST_PARAMS),
    url: endpoints.feeEngine.feeRules.all,
    mapItem: mapFeeRuleToSelectOption,
    enabled,
  });

/**
 * Options d’utilisateurs Keycloak dérivées des listes existantes
 * (`POST /Parties/all` + `POST /Agents/all`) — pas de directory Keycloak
 * dans Postman. Valeur soumise = UUID `sub`.
 */
export const useKeycloakUserSelectOptions = (enabled = true) => {
  const parties = usePagedSelectOptions<PartySelectSource>({
    queryKey: QUERY_KEYS.KYC_PARTIES_LIST(ENTITY_SELECT_LIST_PARAMS),
    url: endpoints.kyc.parties.all,
    mapItem: mapPartyKeycloakToSelectOption,
    enabled,
  });
  const agents = usePagedSelectOptions<AgentSelectSource>({
    queryKey: QUERY_KEYS.KYC_AGENTS_LIST(ENTITY_SELECT_LIST_PARAMS),
    url: endpoints.kyc.agents.all,
    mapItem: mapAgentKeycloakToSelectOption,
    enabled,
  });
  const options = useMemo(() => {
    const seen = new Set<string>();
    const merged: SearchableSelectOption[] = [];
    for (const option of [...parties.options, ...agents.options]) {
      if (seen.has(option.value)) continue;
      seen.add(option.value);
      merged.push(option);
    }
    return merged;
  }, [parties.options, agents.options]);
  return {
    options,
    isLoading: parties.isLoading || agents.isLoading,
  };
};

/** Options de transactions — valeur = référence métier (`POST /Transactions/all`). */
export const useTransactionReferenceSelectOptions = (enabled = true) =>
  usePagedSelectOptions<TransactionSelectSource>({
    queryKey: QUERY_KEYS.WALLET_TRANSACTIONS_LIST(ENTITY_SELECT_LIST_PARAMS),
    url: endpoints.wallet.transactions.all,
    mapItem: mapTransactionReferenceToSelectOption,
    enabled,
  });

/** Options de codes produits — valeur = `code` (`POST /ProductCodes/all`). */
export const useProductCodeCodeSelectOptions = (enabled = true) =>
  usePagedSelectOptions<ProductCodeSelectSource>({
    queryKey: QUERY_KEYS.LEDGER_PRODUCT_CODES_LIST(ENTITY_SELECT_LIST_PARAMS),
    url: endpoints.ledger.productCodes.all,
    mapItem: mapProductCodeCodeToSelectOption,
    enabled,
  });
